import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import apiService, { ScanResult, Vulnerability } from '../services/api';
import WebShieldAssistant from '../components/WebShieldAssistant';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

// Helper function to format a timestamp
const formatTimestamp = (timestamp: Date): string => {
  return new Date(timestamp).toLocaleString();
};

// Helper function to format scan duration
const formatDuration = (durationMs: number): string => {
  if (durationMs < 1000) return `${durationMs}ms`;
  return `${(durationMs / 1000).toFixed(2)}s`;
};

// Helper function to get color for severity
const getSeverityColor = (severity: string): string => {
  switch (severity) {
    case 'critical':
      return '#d32f2f'; // Red
    case 'high':
      return '#f44336'; // Light red
    case 'medium':
      return '#ff9800'; // Orange
    case 'low':
      return '#ffeb3b'; // Yellow
    default:
      return '#4caf50'; // Green
  }
};

// Helper function to get icon for severity
const getSeverityIcon = (severity: string) => {
  switch (severity) {
    case 'critical':
    case 'high':
      return <ErrorIcon />;
    case 'medium':
      return <WarningIcon />;
    case 'low':
      return <InfoIcon />;
    default:
      return <CheckCircleIcon />;
  }
};

const ScanResultPage: React.FC = () => {
  const { scanId } = useParams<{ scanId: string }>();
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pollInterval, setPollInterval] = useState<NodeJS.Timeout | null>(null);
  const [openAIEnabled, setOpenAIEnabled] = useState<boolean>(false);
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const [apiKeyInput, setApiKeyInput] = useState<string>('');
  const [apiKeyError, setApiKeyError] = useState<string | null>(null);
  const [savingSettings, setSavingSettings] = useState<boolean>(false);

  const fetchScanResult = async () => {
    try {
      if (!scanId) {
        setError('Scan ID is required');
        setLoading(false);
        return;
      }

      const result = await apiService.getScanResult(scanId);
      setScanResult(result);

      // If scan is complete or failed, stop polling
      if (result.status === 'completed' || result.status === 'failed') {
        if (pollInterval) {
          clearInterval(pollInterval);
          setPollInterval(null);
        }
      }

      setLoading(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch scan results');
      setLoading(false);
      if (pollInterval) {
        clearInterval(pollInterval);
        setPollInterval(null);
      }
    }
  };

  // Check if OpenAI is enabled
  const checkOpenAIStatus = async () => {
    try {
      const status = await apiService.getOpenAIStatus();
      setOpenAIEnabled(status.enabled);
    } catch (error) {
      console.error('Error checking OpenAI status:', error);
      setOpenAIEnabled(false);
    }
  };

  useEffect(() => {
    fetchScanResult();
    checkOpenAIStatus();

    // Set up polling interval for pending or scanning statuses
    const interval = setInterval(fetchScanResult, 3000);
    setPollInterval(interval);

    // Cleanup interval on component unmount
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [scanId]);

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'pending':
        return <Chip label="Pending" color="warning" variant="outlined" />;
      case 'scanning':
        return <Chip label="Scanning" color="info" />;
      case 'completed':
        return <Chip label="Completed" color="success" />;
      case 'failed':
        return <Chip label="Failed" color="error" />;
      default:
        return <Chip label={status} />;
    }
  };

  // Prepare chart data
  const getChartData = (summary: ScanResult['summary']) => {
    return {
      labels: ['Critical', 'High', 'Medium', 'Low'],
      datasets: [
        {
          data: [summary.critical, summary.high, summary.medium, summary.low],
          backgroundColor: [
            '#d32f2f', // Red (critical)
            '#f44336', // Light red (high)
            '#ff9800', // Orange (medium)
            '#ffeb3b', // Yellow (low)
          ],
          borderWidth: 1,
        },
      ],
    };
  };

  const renderVulnerabilityDetails = (vulnerability: Vulnerability) => {
    return (
      <>
        <Typography variant="body2" paragraph>
          <strong>Description:</strong> {vulnerability.description}
        </Typography>
        
        {vulnerability.foundAt && (
          <Typography variant="body2" paragraph>
            <strong>Found at:</strong> {vulnerability.foundAt}
          </Typography>
        )}

        {vulnerability.details?.vulnerableEndpoints && vulnerability.details.vulnerableEndpoints.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Vulnerable Endpoints:</strong>
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#fff8e1' }}>
              <List dense disablePadding>
                {(vulnerability.details.vulnerableEndpoints as string[]).slice(0, 5).map((endpoint, i) => (
                  <ListItem key={i} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <WarningIcon color="warning" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={endpoint} 
                      primaryTypographyProps={{ 
                        variant: 'body2',
                        sx: { 
                          fontFamily: 'monospace', 
                          fontSize: '0.85rem',
                          wordBreak: 'break-all'
                        } 
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Box>
        )}
        
        {vulnerability.cvss && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>CVSS Score:</strong> {vulnerability.cvss.score.toFixed(1)} 
              <Chip 
                size="small" 
                label={getCvssLabel(vulnerability.cvss.score)} 
                sx={{ 
                  ml: 1, 
                  backgroundColor: getCvssColor(vulnerability.cvss.score),
                  color: 'white'
                }} 
              />
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
              <strong>Vector:</strong> {vulnerability.cvss.vector}
            </Typography>
          </Box>
        )}
        
        {vulnerability.cwe && (
          <Typography variant="body2" paragraph>
            <strong>CWE:</strong> <Link href={`https://cwe.mitre.org/data/definitions/${vulnerability.cwe.replace('CWE-', '')}.html`} target="_blank" rel="noopener">
              {vulnerability.cwe}
            </Link>
          </Typography>
        )}
        
        <Typography variant="body2" paragraph>
          <strong>Recommendation:</strong> {vulnerability.recommendation}
        </Typography>

        {vulnerability.details?.exploitInfo && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Exploit Information:</strong>
            </Typography>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                backgroundColor: '#fef5f5', 
                borderLeft: '4px solid #f44336',
                mt: 1
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {vulnerability.details.exploitInfo}
              </Typography>
              
              {vulnerability.details.exploitExample && (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed rgba(0,0,0,0.1)' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    Example:
                  </Typography>
                  <Paper sx={{ p: 1, backgroundColor: 'rgba(0,0,0,0.04)' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                      {vulnerability.details.exploitExample}
                    </Typography>
                  </Paper>
                </Box>
              )}
              
              {vulnerability.details.exploitScenario && (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed rgba(0,0,0,0.1)' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    Attack Scenario:
                  </Typography>
                  <Typography variant="body2">
                    {vulnerability.details.exploitScenario}
                  </Typography>
                </Box>
              )}
              
              {vulnerability.details.exploitSteps && (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed rgba(0,0,0,0.1)' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    Attack Steps:
                  </Typography>
                  <List dense disablePadding>
                    {(vulnerability.details.exploitSteps as string[]).map((step, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Typography variant="caption">{idx + 1}.</Typography>
                        </ListItemIcon>
                        <ListItemText 
                          primary={step}
                          primaryTypographyProps={{ variant: 'body2' }} 
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Paper>
          </Box>
        )}
        
        {vulnerability.falsePositivePotential && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="body2">
              <strong>False Positive Potential:</strong>
            </Typography>
            <Chip 
              size="small" 
              label={vulnerability.falsePositivePotential} 
              color={
                vulnerability.falsePositivePotential === 'high' ? 'error' : 
                vulnerability.falsePositivePotential === 'medium' ? 'warning' : 'success'
              }
              variant="outlined"
            />
          </Box>
        )}
        
        {vulnerability.exploitability && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="body2">
              <strong>Exploitability:</strong>
            </Typography>
            <Chip 
              size="small" 
              label={vulnerability.exploitability} 
              color={
                vulnerability.exploitability === 'easy' ? 'error' : 
                vulnerability.exploitability === 'moderate' ? 'warning' : 'success'
              }
              variant="outlined"
            />
          </Box>
        )}
        
        {vulnerability.remediationComplexity && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="body2">
              <strong>Remediation Complexity:</strong>
            </Typography>
            <Chip 
              size="small" 
              label={vulnerability.remediationComplexity} 
              color={
                vulnerability.remediationComplexity === 'high' ? 'error' : 
                vulnerability.remediationComplexity === 'medium' ? 'warning' : 'success'
              }
              variant="outlined"
            />
          </Box>
        )}
        
        {vulnerability.details && Object.keys(vulnerability.details).length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Additional Details:</strong>
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
              {vulnerability.details.commentSamples && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom><strong>Comment Samples:</strong></Typography>
                  {vulnerability.details.commentSamples.map((comment: string, i: number) => (
                    <Box key={i} sx={{ mb: 1 }}>
                      <Paper sx={{ p: 1, backgroundColor: '#fff' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
                          {comment}
                        </Typography>
                      </Paper>
                    </Box>
                  ))}
                </Box>
              )}
              
              {vulnerability.details.sampleEmails && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Email Samples:</strong> (from total of {vulnerability.details.emailCount || 'unknown'})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {vulnerability.details.sampleEmails.map((email: string, i: number) => (
                      <Chip key={i} label={email} size="small" />
                    ))}
                  </Box>
                </Box>
              )}
              
              {vulnerability.details.exposedPaths && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom><strong>Exposed Paths:</strong></Typography>
                  {vulnerability.details.exposedPaths.map((path: string, i: number) => (
                    <Typography key={i} variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {path}
                    </Typography>
                  ))}
                </Box>
              )}
              
              {vulnerability.details.systemInfo && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>System Information:</strong> {vulnerability.details.systemInfo}
                  </Typography>
                </Box>
              )}
              
              {vulnerability.details.sensitiveInfoTypes && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom><strong>Sensitive Information Types:</strong></Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {vulnerability.details.sensitiveInfoTypes.map((type: string, i: number) => (
                      <Chip key={i} label={type} size="small" color="warning" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}
              
              {vulnerability.details.remediationSteps && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom><strong>Remediation Steps:</strong></Typography>
                  <List dense disablePadding>
                    {vulnerability.details.remediationSteps.map((step: string, i: number) => (
                      <ListItem key={i} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={step} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
              
              {vulnerability.details.remediationExample && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom><strong>Remediation Example:</strong></Typography>
                  <Paper sx={{ p: 1, backgroundColor: '#e8f5e9' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {vulnerability.details.remediationExample}
                    </Typography>
                  </Paper>
                </Box>
              )}
              
              {vulnerability.details.samplesFound && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom><strong>Samples Found:</strong></Typography>
                  <List dense disablePadding>
                    {vulnerability.details.samplesFound.map((sample: string, i: number) => (
                      <ListItem key={i} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <ErrorIcon color="error" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={sample} 
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            sx: { fontFamily: 'monospace', fontSize: '0.75rem' }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
              
              {vulnerability.details.additionalRisks && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom><strong>Additional Risks:</strong></Typography>
                  <Typography variant="body2">{vulnerability.details.additionalRisks}</Typography>
                </Box>
              )}
              
              {vulnerability.details.note && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {vulnerability.details.note}
                </Typography>
              )}
            </Paper>
          </Box>
        )}
      </>
    );
  };

  // Helper function to get CVSS score label
  const getCvssLabel = (score: number): string => {
    if (score >= 9.0) return 'Critical';
    if (score >= 7.0) return 'High';
    if (score >= 4.0) return 'Medium';
    if (score >= 0.1) return 'Low';
    return 'None';
  };

  // Helper function to get CVSS score color
  const getCvssColor = (score: number): string => {
    if (score >= 9.0) return '#d32f2f'; // Critical - Dark Red
    if (score >= 7.0) return '#f44336'; // High - Red
    if (score >= 4.0) return '#ff9800'; // Medium - Orange
    if (score >= 0.1) return '#ffeb3b'; // Low - Yellow
    return '#4caf50'; // None - Green
  };

  // Toggle OpenAI settings dialog
  const toggleSettings = () => {
    setSettingsOpen(!settingsOpen);
  };

  // Reset settings form
  const resetSettingsForm = () => {
    setApiKeyInput('');
    setApiKeyError(null);
  };

  // Handle settings form submission (this would be implemented server-side)
  const handleSettingsSubmit = () => {
    // This is a placeholder for demo purposes
    // In a real implementation, this would send the API key to the server
    alert('In a production environment, this would update the OpenAI API key on the server.');
    setSettingsOpen(false);
    resetSettingsForm();
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
          }}
        >
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h5" sx={{ mt: 3 }}>
            {scanResult?.status === 'scanning'
              ? 'Scanning website for vulnerabilities...'
              : 'Loading scan results...'}
          </Typography>
          {scanResult?.status === 'scanning' && (
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              This may take a minute depending on the website.
            </Typography>
          )}
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 2,
            backgroundColor: 'white',
            mt: 4,
          }}
        >
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button
            component={RouterLink}
            to="/"
            startIcon={<ArrowBackIcon />}
            variant="contained"
          >
            Back to Home
          </Button>
        </Paper>
      </Container>
    );
  }

  if (!scanResult) {
    return (
      <Container maxWidth="lg">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 2,
            backgroundColor: 'white',
            mt: 4,
          }}
        >
          <Alert severity="warning" sx={{ mb: 3 }}>
            No scan result found for the provided ID.
          </Alert>
          <Button
            component={RouterLink}
            to="/"
            startIcon={<ArrowBackIcon />}
            variant="contained"
          >
            Back to Home
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Button
            component={RouterLink}
            to="/"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            sx={{ mr: 2 }}
          >
            Back to Home
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            variant="outlined"
            onClick={fetchScanResult}
            disabled={
              scanResult?.status === 'pending' || scanResult?.status === 'scanning'
            }
          >
            Refresh Results
          </Button>
        </Box>
        
        {/* Add AI Assistant toggle */}
        {scanResult && scanResult.status === 'completed' && (
          <FormControlLabel
            control={
              <Switch
                checked={openAIEnabled}
                onChange={(e) => setOpenAIEnabled(e.target.checked)}
                color="primary"
              />
            }
            label="Enable WebShield AI Assistant"
          />
        )}
      </Box>

      <Paper
        elevation={3}
        sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor: 'white',
          mb: 4,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            mb: 3,
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom>
            Scan Results
          </Typography>
          {getStatusChip(scanResult.status)}
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" color="text.secondary">
                URL
              </Typography>
              <Typography variant="h6" component="p">
                {scanResult.url}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" color="text.secondary">
                Scan ID
              </Typography>
              <Typography variant="body1">
                {scanResult.id}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" color="text.secondary">
                Timestamp
              </Typography>
              <Typography variant="body1">
                {formatTimestamp(scanResult.timestamp)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" color="text.secondary">
                Scan Duration
              </Typography>
              <Typography variant="body1">
                {scanResult.scanDuration
                  ? formatDuration(scanResult.scanDuration)
                  : 'N/A'}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {scanResult.error && (
          <Alert severity="error" sx={{ mt: 3, mb: 3 }}>
            {scanResult.error}
          </Alert>
        )}
      </Paper>

      {scanResult.status === 'completed' && (
        <>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Card elevation={3} sx={{ borderRadius: 2, mb: 4 }}>
                <CardContent>
                  <Typography variant="h5" component="h2" gutterBottom>
                    Target Information
                  </Typography>
                  {scanResult.targetMetadata ? (
                    <Grid container spacing={2}>
                      {scanResult.targetMetadata.ipAddress && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Typography variant="subtitle2" color="text.secondary">IP Address</Typography>
                          <Typography variant="body1">{scanResult.targetMetadata.ipAddress}</Typography>
                        </Grid>
                      )}
                      
                      {scanResult.targetMetadata.serverInfo && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Typography variant="subtitle2" color="text.secondary">Server</Typography>
                          <Typography variant="body1">{scanResult.targetMetadata.serverInfo}</Typography>
                        </Grid>
                      )}
                      
                      {scanResult.targetMetadata.statusCode && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Typography variant="subtitle2" color="text.secondary">Status Code</Typography>
                          <Typography variant="body1">{scanResult.targetMetadata.statusCode}</Typography>
                        </Grid>
                      )}
                      
                      {scanResult.targetMetadata.responseTime && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Typography variant="subtitle2" color="text.secondary">Response Time</Typography>
                          <Typography variant="body1">{scanResult.targetMetadata.responseTime}ms</Typography>
                        </Grid>
                      )}
                      
                      {scanResult.targetMetadata.technologies && scanResult.targetMetadata.technologies.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Detected Technologies
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {scanResult.targetMetadata.technologies.map((tech, index) => (
                              <Chip 
                                key={index} 
                                label={tech} 
                                variant="outlined" 
                                color="primary" 
                                size="small" 
                              />
                            ))}
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  ) : (
                    <Typography variant="body1">No target metadata available.</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card
                elevation={3}
                sx={{ height: '100%', borderRadius: 2, position: 'relative' }}
              >
                <CardContent>
                  <Typography
                    variant="h5"
                    component="h2"
                    gutterBottom
                    align="center"
                  >
                    Summary
                  </Typography>
                  <Box
                    sx={{
                      height: 230,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    {scanResult.summary.total > 0 ? (
                      <Doughnut
                        data={getChartData(scanResult.summary)}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: 'bottom',
                            },
                          },
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CheckCircleIcon
                          color="success"
                          sx={{ fontSize: 60, mb: 2 }}
                        />
                        <Typography variant="h6" align="center">
                          No vulnerabilities found!
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary="Total Vulnerabilities"
                        secondary={scanResult.summary.total}
                      />
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemIcon sx={{ color: getSeverityColor('critical') }}>
                        <ErrorIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Critical"
                        secondary={scanResult.summary.critical}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ color: getSeverityColor('high') }}>
                        <ErrorIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="High"
                        secondary={scanResult.summary.high}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ color: getSeverityColor('medium') }}>
                        <WarningIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Medium"
                        secondary={scanResult.summary.medium}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ color: getSeverityColor('low') }}>
                        <InfoIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Low"
                        secondary={scanResult.summary.low}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={8}>
              <Card
                elevation={3}
                sx={{ borderRadius: 2, minHeight: '500px' }}
              >
                <CardContent>
                  <Typography
                    variant="h5"
                    component="h2"
                    gutterBottom
                    sx={{ mb: 3 }}
                  >
                    Vulnerabilities
                  </Typography>

                  {scanResult.vulnerabilities.length === 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 8,
                      }}
                    >
                      <CheckCircleIcon
                        color="success"
                        sx={{ fontSize: 80, mb: 3 }}
                      />
                      <Typography variant="h5" gutterBottom align="center">
                        No vulnerabilities detected!
                      </Typography>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        align="center"
                      >
                        The website appears to be secure against the vulnerabilities we check for.
                      </Typography>
                    </Box>
                  ) : (
                    <div>
                      {['critical', 'high', 'medium', 'low'].map((severity) => {
                        const vulnsForSeverity = scanResult.vulnerabilities.filter(
                          (v) => v.severity === severity
                        );

                        if (vulnsForSeverity.length === 0) return null;

                        return (
                          <Box key={severity} sx={{ mb: 3 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                color: getSeverityColor(severity),
                                display: 'flex',
                                alignItems: 'center',
                                mb: 1,
                              }}
                            >
                              {getSeverityIcon(severity)}
                              <span style={{ marginLeft: '8px' }}>
                                {severity.charAt(0).toUpperCase() + severity.slice(1)} Severity
                              </span>
                            </Typography>

                            {vulnsForSeverity.map((vuln: Vulnerability) => (
                              <Accordion key={vuln.id} sx={{ mb: 1 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                  <Typography fontWeight="medium">
                                    {vuln.name}
                                  </Typography>
                                  {vuln.cwe && (
                                    <Chip 
                                      size="small" 
                                      label={vuln.cwe} 
                                      sx={{ ml: 1, fontSize: '0.7rem' }}
                                      variant="outlined" 
                                    />
                                  )}
                                </AccordionSummary>
                                <AccordionDetails>
                                  {renderVulnerabilityDetails(vuln)}
                                </AccordionDetails>
                              </Accordion>
                            ))}
                          </Box>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* Add the WebShield Assistant only when scan is completed, we have a valid scanId, and OpenAI is enabled */}
      {scanResult && scanResult.status === 'completed' && scanId && (
        <WebShieldAssistant scanId={scanId} isEnabled={openAIEnabled} />
      )}
    </Container>
  );
};

export default ScanResultPage; 